import React, {useEffect, useMemo, useState} from 'react';
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLocation,
    useRouteLoaderData,
    useRouteError,
    isRouteErrorResponse,
    redirect,
} from 'react-router';
import {I18nextProvider} from 'react-i18next';
import {Provider} from 'react-redux';
import {createI18n} from './i18n';
import {createAppStore} from './store';
import {startConsentTracking} from './tracking';
import Gdpr from './components/Gdpr';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import {DEFAULT_LANGUAGE, LANGUAGES, isSupportedLanguage} from './languages';
import english from './translations/en.json';
import organization from './organization.json';
import productImage from './productImage';
import dmSans from '@fontsource-variable/dm-sans/files/dm-sans-latin-wght-normal.woff2?url';
import spaceGrotesk from '@fontsource-variable/space-grotesk/files/space-grotesk-latin-wght-normal.woff2?url';
import './scss/index.scss';

const icon = '/public/favicons/heart-cities.svg';

export function headers({errorHeaders}) {
    return {
        'Cache-Control': 'no-store',
        ...(errorHeaders?.has('Retry-After') ? {'Retry-After': errorHeaders.get('Retry-After')} : {}),
    };
}

export async function loader({request}) {
    const url = new URL(request.url);
    if (url.pathname.length > 1 && url.pathname.endsWith('/')) {
        const pathname = url.pathname.replace(/^\/+/, '/').replace(/\/+$/, '') || '/';
        throw redirect(pathname + url.search, {status: 301, headers: {'Cache-Control': 'no-store'}});
    }
    const segment = url.pathname.split('/')[1];
    const lang = isSupportedLanguage(segment) ? segment : DEFAULT_LANGUAGE;
    const messages = await import(`./translations/${lang}.json`);
    return {lang, resources: {[DEFAULT_LANGUAGE]: {translation: english}, [lang]: {translation: messages.default}}};
}

function AppProviders({children, lang, resources}) {
    const i18n = useMemo(() => createI18n(lang, resources), [lang, resources]);
    const [store] = useState(createAppStore);
    const {hash, key} = useLocation();
    useEffect(startConsentTracking, []);
    useEffect(() => {
        if (!hash) return;
        const frame = requestAnimationFrame(() => {
            const section = document.getElementById(hash.slice(1));
            (section?.querySelector('h2') || section)?.scrollIntoView({block: 'start'});
        });
        return () => cancelAnimationFrame(frame);
    }, [hash, key]);
    return (
        <I18nextProvider i18n={i18n}>
            <Provider store={store}>
                {children}
                <Gdpr />
            </Provider>
        </I18nextProvider>
    );
}

export function Layout({children}) {
    const data = useRouteLoaderData('root') || {
        lang: DEFAULT_LANGUAGE,
        resources: {[DEFAULT_LANGUAGE]: {translation: english}},
    };
    const {pathname} = useLocation();
    const home = pathname === '/' || LANGUAGES.some(({code}) => pathname === `/${code}`);
    return (
        <html lang={data.lang} data-bs-theme="light">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
                <meta name="theme-color" content="#fcf9fb" />
                <meta name="color-scheme" content="light" />
                <meta name="facebook-domain-verification" content="lohx38j238iziubov8tgapyg9ezu38" />
                <link rel="preload" as="font" type="font/woff2" href={dmSans} crossOrigin="anonymous" />
                <link rel="preload" as="font" type="font/woff2" href={spaceGrotesk} crossOrigin="anonymous" />
                <link rel="preconnect" href={new URL(process.env.YLP_API_HOST).origin} crossOrigin="anonymous" />
                {home && (
                    <link
                        rel="preload"
                        as="image"
                        fetchPriority="high"
                        media="(min-width: 640px)"
                        href={productImage.src}
                        imageSrcSet={productImage.srcSet}
                        imageSizes={productImage.sizes}
                    />
                )}
                <link rel="icon" href={icon} type="image/svg+xml" sizes="any" />
                <link rel="shortcut icon" href="/public/favicons/heart-cities.ico" />
                <link rel="icon" type="image/png" href="/public/favicons/heart-cities-32.png" sizes="32x32" />
                <link rel="icon" type="image/png" href="/public/favicons/heart-cities-16.png" sizes="16x16" />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="/public/favicons/apple-touch-icon.png?v=city-heart-1"
                />
                <link rel="manifest" href="/public/favicons/site.webmanifest?v=city-heart-1" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(organization)}} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'WebSite',
                            '@id': 'https://yourlovely.place/#website',
                            url: 'https://yourlovely.place/',
                            name: 'YourLovely.Place',
                            inLanguage: LANGUAGES.map(({code}) => code),
                            publisher: {'@id': 'https://yourlovely.place/#organization'},
                        }),
                    }}
                />
                <Meta />
                <Links />
                <link
                    rel="help"
                    type="text/plain"
                    href="/llms.txt"
                    title="About YourLovely.Place for language models"
                />
            </head>
            <body>
                <noscript>Enable JavaScript to customize, preview and purchase your digital poster.</noscript>
                <AppProviders {...data}>{children}</AppProviders>
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}
export default function Root() {
    return <Outlet />;
}
export function ErrorBoundary() {
    const error = useRouteError();
    return <ErrorPage status={isRouteErrorResponse(error) ? error.status : 500} />;
}
